import { ApprovalStatePolicy } from '@/types/approvals'

export type ApprovalProgress = {
  approved: number
  needed: number
  rejected: number
}

export function approvalProgress(
  policy: ApprovalStatePolicy,
): ApprovalProgress {
  const approverCount = policy.approvers.length
  const needed =
    policy.approval_order === 'sequential'
      ? approverCount
      : policy.approvals_required ?? approverCount

  return {
    approved: policy.approvals_received,
    needed,
    rejected: policy.approvers.filter(
      (approver) =>
        approver.kind === 'user' &&
        approver.latest_action?.type === 'rejection',
    ).length,
  }
}

export const REVIEWER_AVATAR_SIZE = 30
export const REVIEWER_AVATAR_OVERLAP = 11
export const REVIEWER_CHEVRON_WIDTH = 14
export const MAX_VISIBLE_REVIEWERS = 5

const OVERFLOW_CHAR_WIDTH = 7
/** Content room the "Reviewers" header needs to avoid being truncated. */
const REVIEWERS_MIN_WIDTH = 70
/**
 * Horizontal padding on a reviewers cell: the `px: 1.25` every cell carries
 * plus the extra `pr: 2.5` the last column gets in ApprovalSection. The table
 * sizes cells border-box, so the column has to pay for this on top of the
 * avatars or the padding eats into them.
 */
const REVIEWERS_CELL_PADDING = 30

/**
 * Must match how ApprovalReviewers actually draws the avatars. Every row in a
 * section shares one column width, so a wrong number here leaves a gap or
 * clips an avatar.
 */
export function reviewersWidth(policy: ApprovalStatePolicy): number {
  const count = policy.approvers.length
  if (count === 0) return 0

  const visible = Math.min(count, MAX_VISIBLE_REVIEWERS)
  const hidden = count - visible
  const isSequential = policy.approval_order === 'sequential'
  const step = isSequential
    ? REVIEWER_AVATAR_SIZE + REVIEWER_CHEVRON_WIDTH
    : REVIEWER_AVATAR_SIZE - REVIEWER_AVATAR_OVERLAP

  let width = REVIEWER_AVATAR_SIZE + (visible - 1) * step
  if (hidden > 0) {
    width +=
      (isSequential ? REVIEWER_CHEVRON_WIDTH + 2 : 6) +
      OVERFLOW_CHAR_WIDTH * `+${hidden}`.length
  }
  return width
}

export function maxReviewersWidth(
  policies: (ApprovalStatePolicy | null | undefined)[],
): number {
  const widest = policies.reduce<number>(
    (widest, policy) =>
      policy ? Math.max(widest, reviewersWidth(policy)) : widest,
    REVIEWERS_MIN_WIDTH,
  )
  return widest + REVIEWERS_CELL_PADDING
}
